// eslint-disable-next-line boundaries/external

import type { MetaDescriptor } from "react-router";
import { parsePath } from "ufo";

import type { GetSettingsQuery, Seo_Translations } from "~/core";
import {
  getRootHreflangAlternates,
  type HreflangAlternate,
  type RouteMetaMatch,
  usesAuthoritativeHreflang,
} from "~/core/services/hreflang-map.service";
import type { PageConstructorData } from "~/core/services/page-constructor.service";

import type { Pages } from "../types";
import {
  buildBreadcrumbList,
  containsBreadcrumbList,
} from "./breadcrumb-list.transformer";

type AlternateMetaLink = {
  hreflang?: string | null;
  href?: string | null;
  tagName: "link";
  rel: "alternate";
};

type SeoTranslationWithPublicAlternates = Seo_Translations & {
  alternateLinks?: Array<{
    hreflang?: string | null;
    href?: string | null;
  }> | null;
};

type CmsAlternateInput = {
  alternate_link_id?: {
    hreflang?: string | null;
    href?: string | null;
  } | null;
};

type PublicAlternateInput = {
  hreflang?: string | null;
  href?: string | null;
};

type ManualAlternateInput = CmsAlternateInput | PublicAlternateInput | null;

type SeoImageValue = Seo_Translations["ogImage"] | string | null | undefined;
type SeoJsonLdValue = Seo_Translations["jsonld"] | null | undefined;

const hasContent = (value: string | null | undefined): value is string => {
  return typeof value === "string" && value.trim().length > 0;
};

const pickSeoValue = (
  overrideValue: string | null | undefined,
  pageValue: string | null | undefined,
  fallbackValue: string | null | undefined,
): string | undefined => {
  if (hasContent(overrideValue)) return overrideValue;
  if (hasContent(pageValue)) return pageValue;
  if (hasContent(fallbackValue)) return fallbackValue;

  return undefined;
};

const pickSeoImage = (
  overrideValue: SeoImageValue,
  pageValue: SeoImageValue,
  fallbackValue: SeoImageValue,
): string | undefined => {
  for (const value of [overrideValue, pageValue, fallbackValue]) {
    if (typeof value === "string" && hasContent(value)) {
      return value;
    }
    if (value && typeof value === "object" && hasContent(value.id)) {
      return `/cms-assets/${value.id}`;
    }
  }

  return undefined;
};

function resolveAbsoluteUrl(
  raw: string | null | undefined,
  fallbackAbsoluteUrl: string,
): string | undefined {
  if (!hasContent(raw)) return undefined;

  try {
    return new URL(raw.startsWith("//") ? `https:${raw}` : raw).href;
  } catch {
    try {
      return new URL(raw, fallbackAbsoluteUrl).href;
    } catch {
      return undefined;
    }
  }
}

const pickJsonLd = (
  overrideValue: SeoJsonLdValue,
  pageValue: SeoJsonLdValue,
  fallbackValue: SeoJsonLdValue,
): SeoJsonLdValue => {
  for (const value of [overrideValue, pageValue, fallbackValue]) {
    if (typeof value === "string" && hasContent(value)) return value;
    if (value && typeof value !== "string") return value;
  }

  return undefined;
};

function resolveCanonical(
  raw: string | null | undefined,
  fallbackAbsoluteUrl: string,
): string {
  const candidate = (raw ?? "").trim() || fallbackAbsoluteUrl;
  const parsed = parsePath(candidate);
  const path = parsed.pathname;

  if (/^(https?:)?\/\//i.test(path)) {
    return path.startsWith("//") ? `https:${path}` : path;
  }

  let origin = "";
  try {
    origin = new URL(fallbackAbsoluteUrl).origin;
  } catch {
    origin = "";
  }

  const normalizedPath = path.startsWith("/") ? path : `/${path}`;
  return `${origin}${normalizedPath}`;
}

const DEFAULT_OG_LOCALES: Record<string, string> = {
  en: "en_US",
  es: "es_ES",
  fi: "fi_FI",
  hu: "hu_HU",
  it: "it_IT",
  pl: "pl_PL",
  pt: "pt_PT",
  ru: "ru_RU",
  zh: "zh_CN",
};

function toOgLocale(locale: string | null | undefined): string | undefined {
  if (!hasContent(locale)) return undefined;

  const normalized = locale.trim().replace("-", "_");
  const [language, territory] = normalized.split("_");
  if (!language) return undefined;

  if (territory) {
    return `${language.toLowerCase()}_${territory.toUpperCase()}`;
  }

  return DEFAULT_OG_LOCALES[language.toLowerCase()];
}

function getLanguageCode(language: unknown): string | undefined {
  if (typeof language === "string") return language;
  if (!language || typeof language !== "object") return undefined;

  const code = (language as { code?: unknown }).code;
  return typeof code === "string" ? code : undefined;
}

const isCmsAlternateInput = (
  item: CmsAlternateInput | PublicAlternateInput,
): item is CmsAlternateInput => {
  return "alternate_link_id" in item;
};

const toAlternateMetaLink = (item: ManualAlternateInput): AlternateMetaLink => {
  if (!item) {
    return {
      tagName: "link",
      rel: "alternate",
    };
  }

  if (isCmsAlternateInput(item)) {
    return {
      hreflang: item.alternate_link_id?.hreflang,
      href: item.alternate_link_id?.href,
      tagName: "link",
      rel: "alternate",
    };
  }

  return {
    hreflang: item.hreflang,
    href: item.href,
    tagName: "link",
    rel: "alternate",
  };
};

export function prepareSeoData(
  params: {
    pageSeo: Pages["seo"];
    fallbackSeo?: Pages["seo"];
    originalUrl: string;
    currentLang: string;
    languages: GetSettingsQuery["languages"];
    hreflangAlternates?: Array<HreflangAlternate>;
    matches?: Array<RouteMetaMatch>;
    breadcrumbs?: {
      data: PageConstructorData;
      homeLabel: string | null | undefined;
      forceVisible?: boolean;
    };
  },
  override?: Partial<Seo_Translations>,
): Array<MetaDescriptor> {
  const { pageSeo, fallbackSeo, originalUrl, currentLang, breadcrumbs } =
    params;
  const page = pageSeo?.translations?.[0];
  const fallback = fallbackSeo?.translations?.[0];
  const result: Array<MetaDescriptor> = [];

  const h1 = pickSeoValue(override?.h1, page?.h1, fallback?.h1);
  const title =
    pickSeoValue(override?.title, page?.title, fallback?.title) ?? h1;
  if (title) result.push({ title });

  const description = pickSeoValue(
    override?.description,
    page?.description,
    fallback?.description,
  );
  if (description) {
    result.push({ name: "description", content: description });
  }

  const ogDescription =
    pickSeoValue(
      override?.ogDescription,
      page?.ogDescription,
      fallback?.ogDescription,
    ) ?? description;
  if (ogDescription) {
    result.push({
      property: "og:description",
      content: ogDescription,
    });
  }

  const ogTitle =
    pickSeoValue(override?.ogTitle, page?.ogTitle, fallback?.ogTitle) ?? title;
  if (ogTitle) result.push({ property: "og:title", content: ogTitle });

  const ogImage = resolveAbsoluteUrl(
    pickSeoImage(override?.ogImage, page?.ogImage, fallback?.ogImage),
    originalUrl,
  );
  if (ogImage) result.push({ property: "og:image", content: ogImage });

  const ogType =
    pickSeoValue(override?.ogType, page?.ogType, fallback?.ogType) ?? "website";
  result.push({ property: "og:type", content: ogType });

  const ogUrl =
    resolveAbsoluteUrl(
      pickSeoValue(override?.ogUrl, page?.ogUrl, fallback?.ogUrl),
      originalUrl,
    ) ?? resolveCanonical(undefined, originalUrl);
  result.push({ property: "og:url", content: ogUrl });

  const ogSiteName = pickSeoValue(
    override?.ogSiteName,
    page?.ogSiteName,
    fallback?.ogSiteName,
  );
  if (ogSiteName) {
    result.push({ property: "og:site_name", content: ogSiteName });
  }

  const ogLocale = toOgLocale(
    pickSeoValue(override?.ogLocale, page?.ogLocale, fallback?.ogLocale) ??
      currentLang,
  );
  if (ogLocale) {
    result.push({ property: "og:locale", content: ogLocale });
  }

  const alternateOgLocales = params.languages.reduce<Set<string>>(
    (acc, language) => {
      const locale = toOgLocale(getLanguageCode(language));

      if (hasContent(locale) && locale !== ogLocale) {
        acc.add(locale);
      }

      return acc;
    },
    new Set(),
  );

  for (const locale of alternateOgLocales) {
    result.push({ property: "og:locale:alternate", content: locale });
  }

  const robots = pickSeoValue(override?.robots, page?.robots, fallback?.robots);
  if (robots) result.push({ property: "robots", content: robots });

  const twitterCard = pickSeoValue(
    override?.twitter_card,
    page?.twitter_card,
    fallback?.twitter_card,
  );
  if (twitterCard) {
    result.push({ name: "twitter:card", content: twitterCard });
  }

  const twitterDescription = pickSeoValue(
    override?.twitter_description,
    page?.twitter_description,
    fallback?.twitter_description,
  );
  if (twitterDescription) {
    result.push({
      name: "twitter:description",
      content: twitterDescription,
    });
  }

  const twitterImage = pickSeoImage(
    override?.twitter_image,
    page?.twitter_image,
    fallback?.twitter_image,
  );
  if (twitterImage) {
    result.push({ name: "twitter:image", content: twitterImage });
  }

  const twitterTitle = pickSeoValue(
    override?.twitter_title,
    page?.twitter_title,
    fallback?.twitter_title,
  );
  if (twitterTitle) {
    result.push({ name: "twitter:title", content: twitterTitle });
  }

  const rootMatch = params.matches?.find((match) => match.id === "root");
  const rootData = rootMatch?.loaderData ?? rootMatch?.data;
  const globalCanonical =
    rootData &&
    typeof rootData === "object" &&
    "globalCanonical" in rootData &&
    typeof rootData.globalCanonical === "string"
      ? rootData.globalCanonical
      : null;
  const canonicalUrl =
    globalCanonical && URL.canParse(originalUrl)
      ? globalCanonical + new URL(originalUrl).pathname
      : originalUrl;
  const canonical = hasContent(override?.canonical)
    ? override.canonical
    : page?.canonical;
  result.push({
    tagName: "link",
    rel: "canonical",
    href: resolveCanonical(canonical, canonicalUrl),
  });

  const pagePublicAlternates = (page as SeoTranslationWithPublicAlternates)
    ?.alternateLinks;
  const fallbackPublicAlternates = (
    fallback as SeoTranslationWithPublicAlternates
  )?.alternateLinks;
  const manualAlternates =
    pagePublicAlternates ??
    fallbackPublicAlternates ??
    page?.seo_alternate_links?.items ??
    fallback?.seo_alternate_links?.items ??
    ([] as Array<ManualAlternateInput>);
  const hreflangAlternates =
    params.hreflangAlternates ??
    getRootHreflangAlternates(params.matches ?? []);
  let alternateLinks: Array<AlternateMetaLink> = [];

  if (
    manualAlternates.length > 0 &&
    !usesAuthoritativeHreflang(params.matches ?? [])
  ) {
    alternateLinks = manualAlternates.map(toAlternateMetaLink);
  } else if (hreflangAlternates.length > 0) {
    alternateLinks = hreflangAlternates.map((alt) => ({
      hreflang: alt.hreflang,
      href: alt.href,
      tagName: "link",
      rel: "alternate",
    }));
  }

  for (const item of alternateLinks) {
    if (hasContent(item.hreflang) && hasContent(item.href)) {
      result.push(item);
    }
  }

  const jsonld = pickJsonLd(override?.jsonld, page?.jsonld, fallback?.jsonld);
  if (jsonld) {
    result.push({
      "script:ld+json": jsonld,
    });
  }

  if (breadcrumbs && !containsBreadcrumbList(jsonld)) {
    const breadcrumbList = buildBreadcrumbList({
      data: breadcrumbs.data,
      homeLabel: breadcrumbs.homeLabel,
      originUrl: originalUrl,
      lang: currentLang,
      forceVisible: breadcrumbs.forceVisible,
    });
    if (breadcrumbList) {
      result.push({ "script:ld+json": breadcrumbList });
    }
  }

  return result;
}
